<template>
  <v-form v-model="valid">
    <div v-if="isReportPortalContext">
      <v-row class="mt-4 mb-n4">
        <v-col>
          <v-select
            v-model="userLanguage"
            class="mx-4"
            type="string"
            :disabled="disabled"
            :items="availableLanguages"
            :label="$t('language')"
            outlined
            @change="onChangeLanguage($event)"
          >
            <template #item="{ item }">
              <span> {{ $t(`enums.LanguageCodeEnum.${item}`) }} {{ item | getFlagEmojiByLanguage }} </span>
            </template>
            <template #selection="{ item }">
              <span> {{ $t(`enums.LanguageCodeEnum.${item}`) }} {{ item | getFlagEmojiByLanguage }} </span>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-list-item three-line class="pa-0 ma-0">
        <v-list-item-content>
          <v-card-title>
            {{ $t("components.profile.notifications.assignee") }}
          </v-card-title>
          <v-card-subtitle>
            {{ $t("components.profile.notifications.assigneeExplanation") }}
          </v-card-subtitle>
          <v-list-subtitle>
            <v-checkbox
              class="mx-4 my-n2"
              v-model="assigneeEmail"
              :disabled="disabled"
              :label="$t('components.profile.notifications.email')"
            />
            <tooltip :text="$t('comingSoon')">
              <v-checkbox class="mx-4 my-n2" disabled :label="$t('components.profile.notifications.push')" />
            </tooltip>
          </v-list-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-list-item three-line class="pa-0 ma-0">
        <v-list-item-content>
          <v-card-title>
            {{ $t("components.profile.notifications.mentioned") }}
          </v-card-title>
          <v-card-subtitle>
            {{ $t("components.profile.notifications.mentionedExplanation") }}
          </v-card-subtitle>
          <v-list-subtitle>
            <v-checkbox
              class="mx-4 my-n2"
              v-model="mentionedEmail"
              :disabled="disabled"
              :label="$t('components.profile.notifications.email')"
            />
            <tooltip :text="$t('comingSoon')">
              <v-checkbox class="mx-4 my-n2" disabled :label="$t('components.profile.notifications.push')" />
            </tooltip>
          </v-list-subtitle>
        </v-list-item-content>
      </v-list-item>
    </div>

    <div v-else class="mt-8">
      <v-row>
        <v-select
          v-model="userLanguage"
          class="mx-4"
          type="string"
          :disabled="disabled"
          :items="availableLanguages"
          :label="$t('language')"
          outlined
          @change="onChangeLanguage"
        >
          <template #item="{ item }">
            <span> {{ $t(`enums.LanguageCodeEnum.${item}`) }} {{ item | getFlagEmojiByLanguage }} </span>
          </template>
          <template #selection="{ item }">
            <span> {{ $t(`enums.LanguageCodeEnum.${item}`) }} {{ item | getFlagEmojiByLanguage }} </span>
          </template>
        </v-select>
      </v-row>

      <v-row>
        <v-checkbox
          class="mx-4 my-n2"
          v-model="isSystemEmail"
          disabled
          :label="$t('components.profile.ProfileCard.notificationTypes.system')"
        />
      </v-row>
      <v-row>
        <v-checkbox class="mx-4 my-n2" v-model="marketingOptIn" :disabled="disabled" :label="marketingOptinLabel" />
      </v-row>

      <v-row v-if="partnerNotificationsEnabled">
        <v-card-title>{{ partnerCompanyName }}</v-card-title>
      </v-row>
      <v-row v-if="partnerNotificationsEnabled">
        <v-checkbox
          class="mx-4 my-n2"
          v-model="partnerNotificationsLocal"
          :disabled="disabled"
          :label="$t('components.profile.ProfileCard.notificationTypes.news')"
        />
      </v-row>
    </div>

    <profile-confirmation-row v-if="!isDisabled" :isLoading="isLoading" :valid="valid" @save="save" @abort="abort" />
    <v-card-actions v-if="isDisabled">
      <v-spacer />
      <v-btn :loading="isLoading" color="primary" @click="isDisabled = !isDisabled" data-test-profile-edit>
        {{ $t("components.profile.EditProfileDialog.edit") }}
      </v-btn>
    </v-card-actions>
  </v-form>
</template>

<script lang="ts">
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { Component, Prop } from "vue-property-decorator";
import ProfileConfirmationRow from "./ProfileConfirmationRow.vue";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { ConfigModule } from "@/store/modules/config";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import Tooltip from "@/components/utility/tooltip.vue";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";

@Component({
  components: { ProfileConfirmationRow, Tooltip },
  filters: { getFlagEmojiByLanguage }
})
export default class ProfileNotificationForm extends DarkModeHighlightMixin {
  readonly availableLanguages = ConfigModule.availableLanguages;

  @Prop()
  isSystemEmail!: boolean;

  @Prop()
  disabled!: boolean;

  @Prop({ default: false })
  isLoading!: boolean;

  @Prop()
  isMarketingOptIn!: boolean;

  @Prop()
  partnerNotifications!: boolean;

  @Prop({ default: false })
  partnerNotificationsEnabled!: boolean;

  @Prop({ default: false })
  isAssigneeEmail!: boolean;

  @Prop({ default: false })
  isMentionedEmail!: boolean;

  @Prop()
  language!: LanguageCodeEnum;

  valid = false;

  get userLanguage() {
    return this.language;
  }

  set userLanguage(language: LanguageCodeEnum) {
    this.$emit("update:language", language);
  }

  get partnerCompanyName() {
    return PartnerModule.partner?.companyName || ReportModule.partner?.companyName;
  }

  get marketingOptinLabel() {
    return (
      ReportModule?.partner?.settings?.dataProtectionLabel ||
      this.$t("components.profile.ProfileCard.notificationTypes.news")
    );
  }

  get marketingOptIn() {
    return this.isMarketingOptIn;
  }

  set marketingOptIn(isMarketingOptIn: boolean) {
    this.$emit("update:isMarketingOptIn", isMarketingOptIn);
  }

  get assigneeEmail() {
    return this.isAssigneeEmail;
  }

  set assigneeEmail(isAssigneeEmail: boolean) {
    this.$emit("update:isAssigneeEmail", isAssigneeEmail);
  }

  get mentionedEmail() {
    return this.isMentionedEmail;
  }

  set mentionedEmail(isMentionedEmail: boolean) {
    this.$emit("update:isMentionedEmail", isMentionedEmail);
  }

  get partnerNotificationsLocal() {
    return this.partnerNotifications;
  }

  set partnerNotificationsLocal(value: boolean) {
    this.$emit("update:partnerNotifications", value);
  }

  get isDisabled() {
    return this.disabled;
  }

  set isDisabled(disabled: boolean) {
    this.$emit("update:disabled", disabled);
  }

  get isReportPortalContext() {
    return ConfigModule.appContext === AppContextEnum.REPORT_PORTAL;
  }

  save() {
    this.$emit("save");
  }

  abort() {
    this.$emit("update:disabled", true);
  }

  onChangeLanguage(event: LanguageCodeEnum) {
    ConfigModule.setLocale(event);
  }
}
</script>
